'use client'

import { Fragment, type ReactNode } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
} as const

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  icon?: ReactNode
  headerActions?: ReactNode
  children: ReactNode
  footer?: ReactNode
  maxWidth?: keyof typeof maxWidthClasses
  className?: string
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  headerActions,
  children,
  footer,
  maxWidth = '4xl',
  className,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  'w-full transform overflow-hidden rounded-2xl p-6 text-left align-middle shadow-xl transition-all',
                  maxWidthClasses[maxWidth],
                  className
                )}
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {icon && (
                      <div className="p-2 rounded-lg bg-[var(--color-lime-muted)]">
                        {icon}
                      </div>
                    )}
                    <Dialog.Title as="h3" className="text-2xl font-bold text-[var(--color-lime)]">
                      {title}
                    </Dialog.Title>
                  </div>
                  <div className="flex items-center space-x-4">
                    {headerActions}
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
                      onClick={onClose}
                    >
                      <XMarkIcon className="h-6 w-6 text-[var(--color-text-muted)]" />
                    </button>
                  </div>
                </div>

                <div className="max-h-[75vh] p-2 overflow-y-auto custom-scrollbar">
                  {children}
                </div>

                {footer && (
                  <div className="pt-4 mt-4 border-t border-[var(--color-border)]">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { Modal }
